/* MY BABY: real-time collection + search filtering.

   The theme's <collection-filters-form> already filters over fetch, but every
   tick re-requested the whole page document (~356KB on /collections/all),
   rewrote the sidebar without putting its scroll position or open groups back,
   and scrolled the window even while the mobile drawer covered it. On a phone
   that reads as a full page reload.

   This replaces only the submitForm step. The URL half is kept identical to the
   theme's so sort, price bounds and popstate keep behaving the same; the fetch
   half asks the Section Rendering API for just this section, keeps the drawer
   where the shopper left it, and drops stale responses instead of racing them.

   Load AFTER collection-filters-form.js. */

(function () {
  'use strict';

  var NAME = 'collection-filters-form';
  var SIDEBAR = '[data-collection-sidebar]';
  var SCROLLER = '.collection__sidebar';
  var GRID = '[data-products-grid]';
  var COUNT = '[data-results-count]';
  var ACTIVE = '[data-active-filters]';
  var ACTIVE_COUNT = 'data-active-filters-count';
  var GROUP = 'details[data-collapsible]';
  var BODY = '[data-collapsible-body]';
  var LOADING = 'is-loading';
  var HIDDEN = 'hidden';
  var DEBOUNCE = 90;

  /* One shared request + timer across the element's lifetimes: replacing the
     sidebar destroys and rebuilds the custom element itself, so per-instance
     state would be thrown away exactly when it is needed. */
  var controller = null;
  var timer = null;

  /* Keyed on the facet's field name, because the heading itself is not stable:
     an active facet renders as "מותג(1)", so matching on the label reopened the
     wrong groups (or none) the moment a filter was applied. */
  function groupKey(details, index) {
    var field = details.querySelector('[name^="filter."]');
    if (field && field.name) return field.name;
    var summary = details.querySelector('summary');
    var label = summary ? summary.textContent.replace(/[\s\d()]+/g, '') : '';
    return label || 'group-' + index;
  }

  /* The theme's "show more" hides its own link and reveals the [data-link-hidden]
     rows; both are class toggles the re-render throws away, so a long brand list
     a shopper had opened would fold up on every tick. */
  function showMoreOpen(details) {
    var more = details.querySelector('[data-show-more]');
    return !!(more && more.parentElement && more.parentElement.classList.contains(HIDDEN));
  }

  function snapshot(sidebar) {
    if (!sidebar) return null;
    var scroller = sidebar.querySelector(SCROLLER);
    var open = [];
    var expanded = [];
    Array.prototype.forEach.call(sidebar.querySelectorAll(GROUP), function (details, i) {
      var key = groupKey(details, i);
      if (details.hasAttribute('open')) open.push(key);
      if (showMoreOpen(details)) expanded.push(key);
    });
    return { top: scroller ? scroller.scrollTop : 0, open: open, expanded: expanded };
  }

  function restore(sidebar, snap) {
    if (!sidebar || !snap) return;
    Array.prototype.forEach.call(sidebar.querySelectorAll(GROUP), function (details, i) {
      var key = groupKey(details, i);
      var body = details.querySelector(BODY);
      if (snap.open.indexOf(key) > -1) {
        details.setAttribute('open', 'true');
        if (body) body.style.height = 'auto';
      } else {
        details.removeAttribute('open');
        if (body) body.style.height = '';
      }
      if (snap.expanded.indexOf(key) > -1) {
        var more = details.querySelector('[data-show-more]');
        if (more && more.parentElement) more.parentElement.classList.add(HIDDEN);
        Array.prototype.forEach.call(details.querySelectorAll('[data-link-hidden]'), function (row) {
          row.classList.remove(HIDDEN);
        });
      }
    });
    var scroller = sidebar.querySelector(SCROLLER);
    if (scroller) scroller.scrollTop = snap.top;
  }

  function isMobile() {
    if (window.theme && typeof window.theme.isMobile === 'function') return window.theme.isMobile();
    return window.innerWidth < 750;
  }

  function drawerIsOpen(sidebar) {
    return !!sidebar && sidebar.classList.contains('expanded');
  }

  /* Byte-for-byte the theme's own URL rules: drop the filter/page/sort keys that
     are already in the address bar, re-add whatever the form now holds, and
     collapse a price range that is sitting on both of its bounds. */
  function buildUrl(element) {
    var params = new window.URL(window.location.href).searchParams;
    var present = {};
    params.forEach(function (value, key) { present[key] = value; });
    var current = params.toString();

    if (current.indexOf('filter.') > -1 || current.indexOf('page=') > -1 || current.indexOf('sort_by=') > -1) {
      Object.keys(present).forEach(function (key) {
        if (key.indexOf('filter.') > -1 || key === 'page' || key === 'sort_by') params.delete(key);
      });
    }

    var form = element.form;
    if (form) {
      var data = new window.URLSearchParams(new FormData(form));
      var minEl = form.querySelector('[data-se-min-value]');
      var maxEl = form.querySelector('[data-se-max-value]');
      var min = minEl && minEl.hasAttribute('data-se-min') ? minEl.getAttribute('data-se-min') : '';
      var max = maxEl && maxEl.hasAttribute('data-se-max') ? maxEl.getAttribute('data-se-max') : '';
      var atBounds = 0;

      data.forEach(function (value, key) {
        if ((key.indexOf('filter.') > -1 && value) || (key.indexOf('sort_by') > -1 && value)) {
          params.append(key, value);
          if ((value === min && key === 'filter.v.price.gte') || (value === max && key === 'filter.v.price.lte')) {
            atBounds += 1;
          }
        }
      });

      if (atBounds === 2) {
        params.delete('filter.v.price.gte');
        params.delete('filter.v.price.lte');
      }
    }

    var query = params.toString();
    return query ? '?' + query : window.location.pathname;
  }

  function fetchText(url, signal) {
    return fetch(url, { signal: signal, credentials: 'same-origin' }).then(function (response) {
      if (!response.ok) throw new Error('filters: ' + response.status + ' for ' + url);
      return response.text();
    });
  }

  function apply(container, grid, nav, html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var nextGrid = doc.querySelector(GRID);
    /* A response without a grid means we asked the wrong thing: let the caller
       fall back rather than blanking the page. */
    if (!nextGrid) throw new Error('filters: no product grid in the response');

    var sidebar = container.querySelector(SIDEBAR);
    var wasOpen = drawerIsOpen(sidebar);
    var snap = snapshot(sidebar);

    var count = container.querySelector(COUNT);
    var nextCount = doc.querySelector(COUNT);
    if (count && nextCount) count.innerHTML = nextCount.innerHTML;

    grid.innerHTML = nextGrid.innerHTML;

    var nextSidebar = doc.querySelector(SIDEBAR);
    if (sidebar && nextSidebar) {
      /* This also swaps out the <collection-filters-form> running this code, so
         nothing below may touch `this` — only the captured nodes. */
      sidebar.innerHTML = nextSidebar.innerHTML;
      restore(sidebar, snap);

      var badge = sidebar.querySelector('[' + ACTIVE_COUNT + ']');
      var pills = container.querySelectorAll(ACTIVE);
      if (badge && pills.length) {
        var total = parseInt(badge.getAttribute(ACTIVE_COUNT), 10) || 0;
        Array.prototype.forEach.call(pills, function (pill) {
          pill.textContent = total;
          pill.classList.toggle(HIDDEN, total < 1);
        });
      }
    }

    /* Only pull the page up when the grid is actually off screen and nothing is
       covering it. Scrolling underneath an open phone drawer is most of what
       made every tap feel like a reload; on desktop the sidebar is inline and
       carries the same "expanded" class permanently, so it never counts. */
    var covered = wasOpen && isMobile();
    if (nav && !covered && window.theme && typeof window.theme.scrollTo === 'function') {
      var top = grid.getBoundingClientRect().top;
      if (top < 0) window.theme.scrollTo(top - nav.offsetHeight);
    }

    grid.classList.remove(LOADING);
    document.dispatchEvent(new CustomEvent('mb:filters:updated', {
      detail: { url: window.location.href }
    }));
  }

  function run(container, grid, nav) {
    if (controller) controller.abort();
    controller = new AbortController();
    var signal = controller.signal;

    var url = window.location.pathname + window.location.search;
    var sectionId = container.getAttribute('data-section-id');
    var sectionUrl = sectionId
      ? url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId)
      : url;

    /* A network failure (no response at all) is not the same as a bad response.
       Navigating on it hands the shopper the browser's offline page and throws
       away the grid, the open drawer and every filter they ticked. Retry once,
       then keep what is on screen; the next tick tries again anyway. */
    function isNetworkError(error) {
      return !!error && error.name === 'TypeError';
    }

    function giveUp() {
      grid.classList.remove(LOADING);
    }

    function attempt(retried) {
      return fetchText(sectionUrl, signal)
        .then(function (html) { apply(container, grid, nav, html); })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (isNetworkError(error)) {
            if (retried) return giveUp();
            return new Promise(function (resolve) { setTimeout(resolve, 1200); }).then(function () {
              if (signal.aborted) return;
              return attempt(true);
            });
          }
          /* The server answered but not with a usable section: retry the whole
             document the way the theme used to, and only if that is unusable
             too hand the URL to the browser, so nobody is left on a stale grid. */
          return fetchText(url, signal)
            .then(function (html) { apply(container, grid, nav, html); })
            .catch(function (fallbackError) {
              if (fallbackError && fallbackError.name === 'AbortError') return;
              if (isNetworkError(fallbackError)) return giveUp();
              grid.classList.remove(LOADING);
              window.location.href = url;
            });
        });
    }

    attempt(false);
  }

  function schedule(element) {
    var container = element.container;
    var grid = element.productsContainer;
    var nav = element.collectionNav;
    if (!container || !grid) return;

    grid.classList.add(LOADING);
    if (timer) clearTimeout(timer);
    /* Coalesces the burst a price slider fires, and a shopper ticking two boxes
       in quick succession, into one request. */
    timer = setTimeout(function () {
      timer = null;
      run(container, grid, nav);
    }, DEBOUNCE);
  }

  function patch() {
    var Klass = window.customElements && window.customElements.get(NAME);
    if (!Klass || Klass.prototype.mbRealtimeFilters) return;
    if (typeof window.AbortController !== 'function' || typeof window.fetch !== 'function') return;
    Klass.prototype.mbRealtimeFilters = true;

    Klass.prototype.submitForm = function (event, href) {
      if (!event || (event && event.type !== 'popstate')) {
        window.history.pushState(null, '', !href ? buildUrl(this) : href);
      } else if (this.sort) {
        this.onSortCheck(event);
      }
      if (!this.productsContainer) return;
      schedule(this);
    };
  }

  if (window.customElements) {
    if (window.customElements.get(NAME)) {
      patch();
    } else {
      window.customElements.whenDefined(NAME).then(patch);
    }
  }
})();
